import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { ComponentLibraryLoader, BarLoader } from '@unite-us/app-components';
import { trackEvent } from '@unite-us/client-utils';
import { connect } from 'react-redux';
import _ from 'lodash';
import {
  showNCDisplay,
  showNCTracks,
  hasPaymentsUserAccess,
  hasLimitedGenderOptions,
  hasClientIdStandardization,
  cl787SexualityFields,
  pays9123VerifyInsurance as pays9123VerifyInsuranceFlag,
  pays7072DisallowSCCEditsByPlanScoping,
  pays9369ShowExcludedPopulationMessage as pays9369ShowExcludedPopulationMessageFlag,
  cl1859ClientIntakeProfileFields,
  cl1990AccommodationsSectionVisibility,
  pays9502ShowCreatedViaRteMessage as pays9502ShowCreatedViaRteMessageFlag,
  cl1987PeoplePronounsVisibility,
} from 'common/utils/FeatureFlags/flags';
import featureFlag from 'common/utils/FeatureFlag/FeatureFlag';
import { hasFeatureRole } from 'common/utils/Roles/hasFeatureRole';
import Notifier from 'common/helpers/Notifier';
import fetchGroupContact from 'src/actions/Contact/Group/fetchGroupContact';
import { coreApi, apiDefault, corePhase3ClientLegacy } from 'src/api/config';
import * as apiHooks from 'src/api/APIHooks';
import { env } from 'src/config/env';
import './FacesheetProfile.scss';

const LoadedProfile = ComponentLibraryLoader({
  loadFn: () => import('@unite-us/app-client-profile'),
  component: 'AppClientProfile',
  loadingApp: 'app-client',
  source: 'app-client-profile',
  datadogRum,
});

const FacesheetProfile = (props) => {
  const {
    contact = {},
    groupId,
    networkId,
    setHeaderContact,
    ncDisplayOnly,
    token,
    currentEmployee,
    isPaymentsUser,
    shouldShowNCTracks,
    limitedGenderOptions,
    clientIdStandardizationEnabled,
    shouldShowSexualityFields,
    shouldShowClientIntakeProfileFields,
    shouldShowAccommodationsSection,
    hasSocialCareNavigation,
    hasInsuranceVerificationRole,
    pays9123VerifyInsurance,
    disallowSccEditingBySocialPlanScope,
    pays9369ShowExcludedPopulationMessage,
    showCondensedView,
    pays9502ShowCreatedViaRteMessage,
    shouldShowPeoplePronounsFields,
  } = props;
  const [isLoading, setIsLoading] = useState(true);
  const containerRef = useRef(null);

  // Handles both loading detection and contact changes
  useEffect(() => {
    // Activate loading when contact changes
    if (contact?.id) {
      setIsLoading(true);
    }

    if (!containerRef.current) return undefined;

    const observer = new MutationObserver(() => {
      // Check if the micro app has loaded by looking for the facesheet-profile class
      const hasLoadedElements = containerRef.current.querySelector('.facesheet-profile');

      if (hasLoadedElements) {
        // Small delay to ensure complete rendering
        setTimeout(() => setIsLoading(false), 500);
      }
    });

    observer.observe(containerRef.current, {
      childList: true,
      subtree: true,
    });

    // Fallback timeout to prevent infinite loading
    const timeout = setTimeout(() => {
      setIsLoading(false);
    }, 10000);

    return () => {
      observer.disconnect();
      clearTimeout(timeout);
    };
  }, [contact?.id]);

  if (_.isEmpty(contact)) {
    return null;
  }

  return (
    <>
      {isLoading && (
        <BarLoader className="h-60" size="full" />
      )}
      <div className={isLoading ? 'invisible' : 'visible'} ref={containerRef}>
        <LoadedProfile
          contact={contact}
          groupId={groupId}
          networkId={networkId}
          token={token}
          currentEmployee={currentEmployee}
          callbacks={{
            onClientSaved: async ({ message = 'Client Successfully Updated' } = {}) => {
              const response = await props.fetchGroupContact(groupId, contact.id);
              setHeaderContact(response?.data?.data);
              Notifier.dispatch(200, message);
            },
            toast: {
              success: (message) => Notifier.dispatch(200, message),
              error: (message) => Notifier.dispatch(400, message),
              warn: (message) => Notifier.dispatch(100, message),
            },
          }}
          adapters={{
            coreApi,
            coreApiClientBase: apiDefault,
            legacyClient: corePhase3ClientLegacy,
          }}
          hooks={{
            ...apiHooks,
          }}
          trackEvent={trackEvent}
          flags={{
            ncDisplayOnly,
            'pays-1554-nctracks': shouldShowNCTracks,
            'cl-787-sexuality-fields': shouldShowSexualityFields,
            'cl-1859-client-intake-profile-fields': shouldShowClientIntakeProfileFields,
            'cl-1990-accommodations-section-visibility': shouldShowAccommodationsSection,
            'pays-9123-verify-insurance': pays9123VerifyInsurance,
            'pays-7072-disallow-editing-scc-based-on-social-plan-scope': disallowSccEditingBySocialPlanScope,
            'cl-1987-people-pronouns-visibility': shouldShowPeoplePronounsFields,
            isPaymentsUser,
            hasInsuranceVerificationRole,
            limitedGenderOptions,
            clientIdStandardizationEnabled,
            hasSocialCareNavigation,
            'pays-9369-show-excluded-population-message': pays9369ShowExcludedPopulationMessage,
            'pays-9502-show-created-via-rte-message': pays9502ShowCreatedViaRteMessage,
          }}
          constants={{
            CORE_BASE_URL: env.CORE_BASE_URL,
            GOOGLE_MAPS_API_KEY: env.GOOGLE_MAPS_API_KEY,
          }}
          showCondensedView={showCondensedView}
        />
      </div>
    </>
  );
};

FacesheetProfile.propTypes = {
  contact: PropTypes.shape({
    has_military: PropTypes.bool.isRequired,
    full_name: PropTypes.string.isRequired,
    household: PropTypes.shape({
      total: PropTypes.number,
    }).isRequired,
  }).isRequired,
  groupId: PropTypes.string.isRequired,
  networkId: PropTypes.string.isRequired,
  params: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
  setHeaderContact: PropTypes.func.isRequired,
  ncDisplayOnly: PropTypes.bool.isRequired,
  token: PropTypes.string.isRequired,
  currentEmployee: PropTypes.object.isRequired,
  fetchGroupContact: PropTypes.func.isRequired,
  isPaymentsUser: PropTypes.bool.isRequired,
  shouldShowNCTracks: PropTypes.bool.isRequired,
  limitedGenderOptions: PropTypes.bool.isRequired,
  clientIdStandardizationEnabled: PropTypes.bool.isRequired,
  shouldShowSexualityFields: PropTypes.bool.isRequired,
  shouldShowClientIntakeProfileFields: PropTypes.bool.isRequired,
  shouldShowAccommodationsSection: PropTypes.bool.isRequired,
  shouldShowPeoplePronounsFields: PropTypes.bool.isRequired,
  hasSocialCareNavigation: PropTypes.bool.isRequired,
  hasInsuranceVerificationRole: PropTypes.bool.isRequired,
  pays9123VerifyInsurance: PropTypes.bool.isRequired,
  disallowSccEditingBySocialPlanScope: PropTypes.bool.isRequired,
  pays9369ShowExcludedPopulationMessage: PropTypes.bool.isRequired,
  showCondensedView: PropTypes.bool,
  pays9502ShowCreatedViaRteMessage: PropTypes.bool.isRequired,
};

FacesheetProfile.defaultProps = {
  showCondensedView: false,
};

function mapStateToProps(state, ownProps) {
  const {
    contacts,
    session: { groupId, networkId },
  } = state;

  const contactId = _.get(ownProps, 'params.id');
  const contact = ownProps.isClientsDrawer ?
    _.get(ownProps, 'contact') :
    _.find(_.get(contacts, 'contacts', []), { id: contactId });
  const ncDisplayOnly = showNCDisplay(state);
  const isPaymentsUser = hasPaymentsUserAccess(state);
  const limitedGenderOptions = hasLimitedGenderOptions(state);
  const clientIdStandardizationEnabled = hasClientIdStandardization(state);
  const shouldShowSexualityFields = cl787SexualityFields(state);
  const shouldShowClientIntakeProfileFields = cl1859ClientIntakeProfileFields(state);
  const shouldShowAccommodationsSection = cl1990AccommodationsSectionVisibility(state);
  const currentEmployee = state.globalState.currentEmployee;
  const token = state.session.access_token;
  const shouldShowNCTracks = showNCTracks(state);
  const disallowSccEditingBySocialPlanScope = pays7072DisallowSCCEditsByPlanScoping(state);
  const shouldShowPeoplePronounsFields = cl1987PeoplePronounsVisibility(state);
  const hasSocialCareNavigation = hasFeatureRole({
    employeeRoles: currentEmployee?.roles,
    targetRoleKey: 'social_care_navigation',
  });
  const hasInsuranceVerificationRole = hasFeatureRole({
    employeeRoles: currentEmployee?.roles,
    targetRoleKey: 'insurance_verification',
  });
  const pays9369ShowExcludedPopulationMessage = pays9369ShowExcludedPopulationMessageFlag(state);
  const showCondensedView = ownProps.isClientsDrawer ?? false;
  const pays9502ShowCreatedViaRteMessage = pays9502ShowCreatedViaRteMessageFlag(state);

  return {
    contact,
    networkId,
    groupId,
    ncDisplayOnly,
    currentEmployee,
    token,
    isPaymentsUser,
    shouldShowNCTracks,
    limitedGenderOptions,
    clientIdStandardizationEnabled,
    shouldShowSexualityFields,
    shouldShowClientIntakeProfileFields,
    shouldShowAccommodationsSection,
    shouldShowPeoplePronounsFields,
    hasSocialCareNavigation,
    hasInsuranceVerificationRole,
    pays9123VerifyInsurance: pays9123VerifyInsuranceFlag(state),
    disallowSccEditingBySocialPlanScope,
    pays9369ShowExcludedPopulationMessage,
    showCondensedView,
    pays9502ShowCreatedViaRteMessage,
  };
}

export default featureFlag(connect(
  mapStateToProps,
  { fetchGroupContact },
)(FacesheetProfile));
